/**
 * Current maintenance mode status including active and scheduled maintenance windows
 */
export type MaintenanceModeStatus = {
  /** Currently active maintenance window, null if system is not in maintenance */
  active: MaintenanceMode | null;
  /** List of scheduled future maintenance windows */
  scheduled: MaintenanceMode[];
};

/**
 * Maintenance mode record representing a maintenance window
 */
export type MaintenanceMode = {
  /** Unique identifier for the maintenance record */
  id: string;
  /** Reason for the maintenance (10-200 characters) */
  reason: string;
  /** Current status of the maintenance window */
  status: MaintenanceModeStatusEnum;
  /** Whether maintenance is currently active */
  enabled: boolean;
  /** ISO timestamp when maintenance is scheduled to start */
  scheduled_start: string;
  /** ISO timestamp when maintenance is scheduled to end, null for emergency maintenance */
  scheduled_end: string | null;
  /** ISO timestamp when maintenance actually started, null if not yet started */
  actual_start: string | null;
  /** ISO timestamp when maintenance actually ended, null if not yet completed */
  actual_end: string | null;
  /** Reason provided when maintenance was cancelled, null if not cancelled */
  cancellation_reason: string | null;
  /** User who cancelled the maintenance, null if not cancelled */
  cancelled_by: UserInfo | null;
  /** ISO timestamp when maintenance was cancelled, null if not cancelled */
  cancelled_dt: string | null;
  /** User who created the maintenance record */
  created_by: UserInfo;
  /** User who last updated the maintenance record, null if never updated */
  updated_by: UserInfo | null;
  /** User who deleted the maintenance record, null if not deleted */
  deleted_by: UserInfo | null;
  /** ISO timestamp when the maintenance record was created */
  created_dt: string;
  /** ISO timestamp when the maintenance record was last updated, null if never updated */
  updated_dt: string | null;
  /** ISO timestamp when the maintenance record was deleted, null if not deleted */
  deleted_dt: string | null;
  /** Whether the maintenance record has been soft deleted */
  is_deleted: boolean;
};

/**
 * User information associated with maintenance actions
 */
export type UserInfo = {
  /** Unique identifier for the user */
  id: string;
  /** User's first name */
  first_name: string;
  /** User's last name */
  last_name: string;
  /** User's email address */
  email: string;
};

/**
 * Status of a maintenance window
 */
export enum MaintenanceModeStatusEnum {
  /** Maintenance is currently active */
  ACTIVE = 'active',
  /** Maintenance is scheduled for the future */
  SCHEDULED = 'scheduled',
  /** Maintenance has been completed */
  COMPLETED = 'completed',
  /** Maintenance was cancelled before completion */
  CANCELLED = 'cancelled',
}

/**
 * Request payload for enabling emergency maintenance immediately
 */
export type EnableMaintenanceRequest = {
  /** Reason for the maintenance (10-200 characters) */
  reason: string;
  /** ISO timestamp for estimated end time (optional) */
  estimated_end?: string;
};

/**
 * Request payload for scheduling future maintenance
 */
export type ScheduleMaintenanceRequest = {
  /** Reason for the maintenance */
  reason: string;
  /** ISO timestamp when maintenance should start */
  scheduled_start: string;
  /** ISO timestamp when maintenance should end */
  scheduled_end: string;
};

/**
 * Request payload for cancelling scheduled maintenance
 */
export type CancelMaintenanceRequest = {
  /** Optional reason for cancelling the maintenance */
  cancellation_reason?: string;
};

/**
 * Paginated response for maintenance history queries
 */
export type MaintenanceHistoryResponse = {
  /** Array of maintenance records for the current page */
  entries: MaintenanceMode[];
  /** Total number of maintenance records matching the query */
  total: number;
  /** Current page number (1-indexed) */
  page: number;
  /** Number of records per page */
  page_size: number;
};
